import type { ProductImportQuota } from "api-types";
import type { Timestamp } from "firebase/firestore";

import type {
  FirestoreProductUploadDocument,
  ProductUploadRecord,
  ProductUploadStatus,
  UploadPrefill,
} from "@dashboard/types/product-upload";

function normalizeUploadStatus(status: string): ProductUploadStatus | null {
  if (status === "approval_required") {
    return "awaiting_review";
  }

  if (status === "approved") {
    return "completed";
  }

  if (status === "cancelled") {
    return "cancelled";
  }

  if (status === "canceled") {
    return "cancelled";
  }

  if (
    status === "assigned" ||
    status === "in_progress" ||
    status === "awaiting_review" ||
    status === "completed" ||
    status === "rejected" ||
    status === "failed" ||
    status === "cancelled"
  ) {
    return status;
  }

  return null;
}

function timestampToMillis(timestamp: Timestamp | null | undefined) {
  return timestamp ? timestamp.toMillis() : undefined;
}

// The prefill drives both the review screen and the completed summary. Once a
// scan is approved the reviewer's edited ingredient / may-contain lists are
// persisted separately, so prefer those over the raw scan; parseResult remains
// the fallback for scans not yet reviewed.
function buildUploadPrefill(
  doc: FirestoreProductUploadDocument
): UploadPrefill | undefined {
  const { parseResult, reviewedIngredientIds, reviewedMayContainAllergenIds } =
    doc;

  if (!parseResult && !reviewedIngredientIds) {
    return undefined;
  }

  return {
    ingredientIds:
      reviewedIngredientIds ?? parseResult?.igfdIngredientIds ?? [],
    mayContainAllergenIds:
      reviewedMayContainAllergenIds ??
      parseResult?.igfdMayContainAllergenIds ??
      [],
    notFoundIngredients: parseResult?.notFoundIngredients ?? [],
    productName: parseResult?.productName ?? "",
  };
}

export function readProductUploadRecord(
  doc: FirestoreProductUploadDocument
): ProductUploadRecord | null {
  const status = normalizeUploadStatus(doc.status);
  const uploadedAtMillis = timestampToMillis(doc.createdAt);
  const updatedAtMillis = timestampToMillis(doc.updatedAt);
  const photoUploaderUserId =
    doc.photoUploaderUserId ?? doc.photoUplaoderUserId;
  const parseResult = doc.parseResult ?? null;

  if (
    !status ||
    !photoUploaderUserId ||
    uploadedAtMillis === undefined ||
    updatedAtMillis === undefined
  ) {
    return null;
  }

  return {
    id: doc.id,
    restaurantUuid: doc.restaurantUuid,
    productId: doc.productId ?? null,
    productName:
      doc.productName?.trim() ||
      parseResult?.productName?.trim() ||
      "Product upload",
    assignedProductName: doc.assignedProductName?.trim() || null,
    uploadedAtMillis,
    updatedAtMillis,
    status,
    photoUploaderUserId,
    assignedToEmail: doc.assignedToEmail ?? null,
    assignedToName: doc.assignedToName ?? null,
    source: doc.source ?? "dashboard_direct",
    imageUrls: Array.isArray(doc.imageUrls)
      ? doc.imageUrls.filter((url): url is string => typeof url === "string")
      : [],
    parseResult,
    prefill: buildUploadPrefill(doc),
    processedAtMillis: timestampToMillis(doc.processedAt),
    errorMessage: doc.errorMessage ?? null,
  };
}

export function getApprovalRequiredProductUploadCount(
  records: ProductUploadRecord[]
) {
  return records.filter((record) => record.status === "awaiting_review").length;
}

type ProductImportCycle = Pick<
  ProductImportQuota,
  "cycleLengthMonths" | "cycleResetMonth" | "cycleResetDay"
>;

/**
 * Returns the [start, end) millis of the import-quota cycle that contains
 * `nowMillis`, derived from the quota's cycle length and reset anchor.
 * A length of 0 or 1 is treated as monthly (the anchor month is ignored and
 * the quota resets on the reset day every month); longer lengths repeat from
 * the anchor month/day in steps of cycleLengthMonths.
 */
export function getProductImportCycleWindow(
  cycle: ProductImportCycle,
  nowMillis = Date.now()
): { startMillis: number; endMillis: number } {
  const length =
    cycle.cycleLengthMonths >= 1 ? Math.floor(cycle.cycleLengthMonths) : 1;
  const resetDay =
    cycle.cycleResetDay >= 1 ? Math.floor(cycle.cycleResetDay) : 1;
  // Monthly cycles reset every month, so the anchor month is irrelevant.
  const anchorMonthIndex =
    length === 1
      ? 0
      : Math.min(Math.max(Math.floor(cycle.cycleResetMonth) || 1, 1), 12) - 1;

  // Clamp the reset day to the target month's length (e.g. day 31 in February).
  const boundaryAt = (absoluteMonth: number) => {
    const year = Math.floor(absoluteMonth / 12);
    const month = absoluteMonth - year * 12;
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    return new Date(year, month, Math.min(resetDay, lastDayOfMonth));
  };

  const now = new Date(nowMillis);
  const currentAbsoluteMonth = now.getFullYear() * 12 + now.getMonth();
  const monthsIntoCycle =
    (((currentAbsoluteMonth - anchorMonthIndex) % length) + length) % length;
  let startAbsoluteMonth = currentAbsoluteMonth - monthsIntoCycle;
  // If we are earlier in the boundary month than the reset day, the cycle
  // actually started one length ago.
  if (boundaryAt(startAbsoluteMonth).getTime() > nowMillis) {
    startAbsoluteMonth -= length;
  }

  return {
    startMillis: boundaryAt(startAbsoluteMonth).getTime(),
    endMillis: boundaryAt(startAbsoluteMonth + length).getTime(),
  };
}

export function getCurrentCycleProductUploadCount(
  records: ProductUploadRecord[],
  cycle: ProductImportCycle,
  nowMillis = Date.now()
) {
  const { startMillis, endMillis } = getProductImportCycleWindow(
    cycle,
    nowMillis
  );

  return records.filter(
    (record) =>
      record.status !== "assigned" &&
      record.uploadedAtMillis >= startMillis &&
      record.uploadedAtMillis < endMillis
  ).length;
}

export function hasProductImageImportQuotaRemaining({
  limit,
  uploadCountThisCycle,
}: {
  limit: number | null | undefined;
  uploadCountThisCycle: number;
}) {
  return (limit ?? 0) > uploadCountThisCycle;
}

/**
 * How a quota stands right now: scans taken this cycle, scans queued against a
 * manager, and what is left. Queued scans have not been taken yet but each one
 * spends an allowance the moment the manager uploads, so both the workspace's
 * own header and the admin plan page have to subtract them. Kept here so those
 * two surfaces cannot drift apart.
 */
export function getProductImportCycleUsage(
  records: ProductUploadRecord[],
  quota: ProductImportCycle & { limit?: number | null },
  nowMillis = Date.now()
): { used: number; queued: number; remaining: number } {
  const used = getCurrentCycleProductUploadCount(records, quota, nowMillis);
  const queued = records.filter(
    (record) => record.status === "assigned"
  ).length;

  return {
    used,
    queued,
    remaining: Math.max(0, (quota.limit ?? 0) - used - queued),
  };
}

/**
 * Records are written to Firestore as `in_progress` at scan start and only
 * transitioned (to awaiting_review/failed/cancelled) by client-side handlers.
 * If the page is closed, refreshed, or crashes mid-scan, none of those handlers
 * run and the record is stranded as `in_progress` forever. Any `in_progress`
 * record older than this threshold is treated as a failed/abandoned scan.
 */
export const STALE_IN_PROGRESS_UPLOAD_THRESHOLD_MILLIS = 5 * 60 * 1000;

export const STALE_PRODUCT_UPLOAD_ERROR_MESSAGE =
  "This upload didn't finish. Please try again.";

export function isStaleInProgressUpload(
  record: ProductUploadRecord,
  nowMillis = Date.now()
): boolean {
  return (
    record.status === "in_progress" &&
    nowMillis - record.updatedAtMillis >
      STALE_IN_PROGRESS_UPLOAD_THRESHOLD_MILLIS
  );
}

export function markStaleInProgressUploadsFailed(
  records: ProductUploadRecord[],
  nowMillis = Date.now()
): ProductUploadRecord[] {
  return records.map(
    (record): ProductUploadRecord =>
      isStaleInProgressUpload(record, nowMillis)
        ? {
            ...record,
            status: "failed",
            errorMessage:
              record.errorMessage ?? STALE_PRODUCT_UPLOAD_ERROR_MESSAGE,
            updatedAtMillis: nowMillis,
            processedAtMillis: nowMillis,
          }
        : record
  );
}

// Fold a live snapshot into the prior list: keep prior object identity when
// updatedAtMillis is unchanged (so an unrelated update can't reset an open
// editor), and carry over client-only previewImageUrls the server never persists.
export function reconcileUploadSnapshot(
  previous: ProductUploadRecord[],
  incoming: ProductUploadRecord[]
): ProductUploadRecord[] {
  const priorById = new Map(previous.map((record) => [record.id, record]));
  return incoming.map((record) => {
    const prior = priorById.get(record.id);
    if (prior && prior.updatedAtMillis === record.updatedAtMillis) {
      return prior;
    }
    return prior?.previewImageUrls && !record.previewImageUrls
      ? { ...record, previewImageUrls: prior.previewImageUrls }
      : record;
  });
}
